<script setup lang="ts">
import { onMounted, onUnmounted } from 'vue'
import { Head, Link } from '@inertiajs/vue3'
import InternalLayout from '@/layouts/InternalLayout.vue'

interface Author {
  id: number
  name: string
}

interface Post {
  id: number
  title: string
  slug: string
  excerpt: string | null
  content: string
  featured_image: string | null
  featured_image_alt: string | null
  meta_title: string | null
  meta_description: string | null
  canonical_url: string | null
  og_image: string | null
  published_at: string
  author: Author
}

const props = defineProps<{
  post: Post
}>()

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
}

let jsonLdScript: HTMLScriptElement | null = null

onMounted(() => {
  jsonLdScript = document.createElement('script')
  jsonLdScript.type = 'application/ld+json'
  jsonLdScript.textContent = JSON.stringify({
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: props.post.title,
    description: props.post.meta_description || props.post.excerpt || '',
    image: props.post.featured_image ? `${window.location.origin}/storage/${props.post.featured_image}` : undefined,
    datePublished: props.post.published_at,
    author: {
      '@type': 'Person',
      name: props.post.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Mow Managers',
      url: 'https://mowmanagers.com',
    },
  })
  document.head.appendChild(jsonLdScript)
})

onUnmounted(() => {
  jsonLdScript?.remove()
})
</script>

<template>
  <InternalLayout :title="post.title">
    <Head :title="`${post.meta_title || post.title}`">
      <meta v-if="post.meta_description" head-key="description" name="description" :content="post.meta_description" />
      <meta v-else-if="post.excerpt" head-key="description" name="description" :content="post.excerpt" />
      <link rel="canonical" :href="post.canonical_url || `https://mowmanagers.com/${post.slug}`" />
      <meta property="og:title" :content="`${post.meta_title || post.title}`" />
      <meta v-if="post.meta_description || post.excerpt" property="og:description" :content="post.meta_description || post.excerpt || ''" />
      <meta property="og:type" content="article" />
      <meta property="og:url" :content="`https://mowmanagers.com/${post.slug}`" />
      <meta v-if="post.og_image || post.featured_image" property="og:image" :content="`${$page.props.ziggy?.url || ''}/storage/${post.og_image || post.featured_image}`" />
    </Head>

    <article aria-label="Blog post" class="py-16 lg:py-24 bg-white">
      <div class="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
        <div class="flex items-center gap-2 text-[15px] text-[#6d706d]">
          <span>By {{ post.author.name }}</span>
          <span>&middot;</span>
          <time :datetime="post.published_at">{{ formatDate(post.published_at) }}</time>
        </div>

        <img
          v-if="post.featured_image"
          :src="`/storage/${post.featured_image}`"
          :alt="post.featured_image_alt || post.title"
          class="w-full rounded-[20px] mt-8"
        />

        <div class="prose prose-lg max-w-none mt-8" v-html="post.content" />

        <div class="mt-12 pt-8 border-t border-gray-200">
          <Link href="/blog" class="text-mm-green font-bold uppercase tracking-[1.6px] text-sm hover:text-mm-green-dark transition-colors">
            &larr; Back to Blog
          </Link>
        </div>
      </div>
    </article>
  </InternalLayout>
</template>
